import AutoTopUpOfferRow from './AutoTopUpOfferRow';
import React, { useMemo, useState } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import CreditPacksSection from '@/components/Credits/CreditPacksSection';
import CreditsFAQs from '@/components/Credits/CreditsFAQs';
import PurchasedCreditsSection from '@/components/Credits/PurchasedCreditsSection';
import MonthlyCreditsSection from '@/components/Credits/MonthlyCreditsSection';
import NoCreditsSection from '@/components/Credits/NoCreditsSection';
import PlusUpsellBanner from '@/components/Credits/PlusUpsellBanner';
import PremiumUpsellBanner from '@/components/Credits/PremiumUpsellBanner';
import Tabs from '@/components/Tabs';
import Text from '@/components/Typography/Text';
import useTranslation from '@/hooks/useTranslation';

const createStyles = (colors, isXS, isSM) => StyleSheet.create({
  container: {
    width: '100%',
    flex: 1,
    ...(Platform.OS === 'macos' ? { flexBasis: 'auto', flexShrink: 0 } : Platform.OS !== 'web' ? { flexBasis: 0, minHeight: 0 } : {}),
  },
  scrollContainer: {
    flexGrow: 1,
  },
  wrapperContainer: {
    flex: 1,
    ...(Platform.OS === 'macos' && { flexBasis: 'auto', flexShrink: 0 }),
    flexDirection: isXS ? 'column' : 'row',
    width: '100%',
  },
  leftTopContainer: {
    maxWidth: 
      isXS ? '100%' : 
      isSM ? 310 :
      420,
    paddingHorizontal: 
      (isXS || isSM) ? 20 :
      20,
    paddingTop: 
      (isXS || isSM) ? 20 :
      20,
    paddingBottom: 
      isXS ? 0 :
      isSM ? 20 :
      20,
    backgroundColor: isXS ? 'transparent' : colors.solid_color_block,
    width: '100%',
    justifyContent: isXS ? 'center' : 'flex-start',
    alignItems: 'center',
  },
  rightBottomContainer: {
    flex: 1,
    padding: 
      (isXS || isSM) ? 20 :
      20,
    minWidth: 
      isXS ? '100%' :
      isSM ? 0 :
      510,
    maxWidth: 
      isXS ? '100%' :
      isSM ? undefined :
      560,
    marginHorizontal: 'auto',
    width: '100%',
    justifyContent: 'flex-start',
  },
  tabsContainer: {
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  orDivider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 20,
    gap: 12,
  },
  orDividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.divider_color,
  },
  orDividerText: {
    paddingHorizontal: 8,
  },
});

function CreditsPurchaseModalControl({
  isPlus,
  isPlusPremium,
  isIapSubscriber,
  credits,
  onClose,
  hasMonthlyCredits,
  hasPurchasedCredits,
  monthlyCredits,
  purchasedCredits,
  initialTab,
  promoCode,
}) {
  const { colors } = useTheme();
  const { isXS, isSM } = useResponsiveness();
  const { t } = useTranslation();
  const styles = useMemo(() => createStyles(colors, isXS, isSM), [colors, isXS, isSM]);

  // Plus Premium is web-only; never upsell on top of App Store / Play Plus.
  const showPremiumUpsell = Boolean(isPlus && !isPlusPremium && !isIapSubscriber && (Platform.OS === 'web' || Platform.OS === 'macos'));

  const getDefaultTab = () => {
    if (initialTab) return initialTab;
    // Free users on mobile: land on Plus upsell (credits packs are secondary)
    if (!isPlus && isXS) return 'upgrade';
    return 'buy-credits';
  };
  const [activeTab, setActiveTab] = useState(getDefaultTab());

  // Free mobile users always get an Upgrade tab — including when they still
  // have signup/purchased credits. Previously that case only showed
  // My Credits / Buy Credits, so Plus was unreachable on mobile web.
  const mobileTabs = useMemo(() => {
    if (!isPlus) {
      const next = [
        {
          id: 'upgrade',
          label: t('modals.creditsPurchase.upgradeTab'),
          icon: 'sparkles',
          active: activeTab === 'upgrade',
          onPress: () => setActiveTab('upgrade'),
        },
        {
          id: 'buy-credits',
          label: t('modals.creditsPurchase.buyCreditsTab'),
          icon: 'coins',
          active: activeTab === 'buy-credits',
          onPress: () => setActiveTab('buy-credits'),
        },
      ];
      if (hasMonthlyCredits || hasPurchasedCredits) {
        next.push({
          id: 'my-credits',
          label: t('modals.creditsPurchase.myCreditsTab'),
          icon: 'coins',
          active: activeTab === 'my-credits',
          onPress: () => setActiveTab('my-credits'),
        });
      }
      return next;
    }

    return [
      {
        id: 'my-credits',
        label: t('modals.creditsPurchase.myCreditsTab'),
        icon: 'coins',
        active: activeTab === 'my-credits',
        onPress: () => setActiveTab('my-credits'),
      },
      {
        id: 'buy-credits',
        label: t('modals.creditsPurchase.buyCreditsTab'),
        icon: 'plus',
        active: activeTab === 'buy-credits',
        onPress: () => setActiveTab('buy-credits'),
      },
    ];
  }, [
    isPlus,
    activeTab,
    hasMonthlyCredits,
    hasPurchasedCredits,
    t,
  ]);

  const renderOrDivider = () => (
    <View style={styles.orDivider}>
      <View style={styles.orDividerLine} />
      <Text size="s" weight="bold" color="secondary" style={styles.orDividerText}>{t('modals.creditsPurchase.orDivider')}</Text>
      <View style={styles.orDividerLine} />
    </View>
  );

  const renderContent = () => {
    if (!hasMonthlyCredits && !hasPurchasedCredits) {
      if (isXS && !isPlus) {
        return (
          <View style={styles.wrapperContainer}>
            <View style={styles.leftTopContainer}>
              <NoCreditsSection isPlus={isPlus} noButton={true} />
            </View>
            {activeTab === 'upgrade' && (
              <View style={styles.rightBottomContainer}>
                <PlusUpsellBanner onClose={onClose} />
              </View>
            )}
            {activeTab === 'buy-credits' && (
              <View style={styles.rightBottomContainer}>
                <PlusUpsellBanner onClose={onClose} />
                <CreditPacksSection promoCode={promoCode} />
            <AutoTopUpOfferRow />
                <View style={{ display: 'flex', marginVertical: 10 }} />
                <CreditsFAQs />
              </View>
            )}
          </View>
        );
      }
      return (
        <View style={styles.wrapperContainer}>
          <View style={styles.leftTopContainer}>
            <NoCreditsSection isPlus={isPlus} noButton={true} />
            {!isPlus && <PlusUpsellBanner onClose={onClose} />}
            {showPremiumUpsell && <PremiumUpsellBanner onClose={onClose} />}
          </View>
          <View style={styles.rightBottomContainer}>
            <CreditPacksSection promoCode={promoCode} />
            <AutoTopUpOfferRow />
            <View style={{ display: 'flex', marginVertical: 10 }} />
            <CreditsFAQs />
          </View>
        </View>
      );
    }

    if (!isXS) {
      return (
        <View style={styles.wrapperContainer}>
          <View style={styles.leftTopContainer}>
            {hasMonthlyCredits && <MonthlyCreditsSection
              total={monthlyCredits.total}
              used={monthlyCredits.used}
              remaining={monthlyCredits.remaining}
              resetDate={monthlyCredits.resetDate}
            /> }
            {hasPurchasedCredits && <PurchasedCreditsSection credits={purchasedCredits.available} />}
            {!isPlus && <PlusUpsellBanner onClose={onClose} />}
            {showPremiumUpsell && <PremiumUpsellBanner onClose={onClose} />}
          </View>
          <View style={styles.rightBottomContainer}>
            <CreditPacksSection promoCode={promoCode} />
            <AutoTopUpOfferRow />
            <View style={{ display: 'flex', marginVertical: 10 }} />
            <CreditsFAQs />
          </View>
        </View>
      );
    }

    return (
      <View style={styles.wrapperContainer}>
        {activeTab === 'my-credits' && (
          <View style={styles.leftTopContainer}>
            {hasMonthlyCredits && <MonthlyCreditsSection 
              total={monthlyCredits.total}
              used={monthlyCredits.used}
              remaining={monthlyCredits.remaining}
              resetDate={monthlyCredits.resetDate}
            /> }
            {hasPurchasedCredits && <PurchasedCreditsSection credits={purchasedCredits.available} />}
          </View>
        )}
        {activeTab === 'buy-credits' && (
          <View style={styles.rightBottomContainer}>
            {!isPlus && <PlusUpsellBanner onClose={onClose} />}
            {showPremiumUpsell && <PremiumUpsellBanner onClose={onClose} />}
            <CreditPacksSection promoCode={promoCode} />
            <AutoTopUpOfferRow />
            <View style={{ display: 'flex', marginVertical: 10 }} />
            <CreditsFAQs />
          </View>
        )}
        {activeTab === 'upgrade' && (
          <View style={styles.rightBottomContainer}>
            <PlusUpsellBanner onClose={onClose} />
          </View>
        )}
      </View>
    );
  };

  return (
    <View style={[styles.container, styles.scrollContainer]}>
      {isXS && (
        <View style={styles.tabsContainer}>
          <Tabs tabs={mobileTabs} />
        </View>
      )}
      {renderContent()}
    </View>
  );
}

export default React.memo(CreditsPurchaseModalControl);
